import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { ChevronRight, CheckCircle2, AlertCircle, Tag, Building2, Package, ShoppingCart, Plus, Minus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { resolveImageUrl } from '../components/ImageUploader';
import { usePricing } from '../lib/pricing';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const { toast } = useToast();
  const { addItem, items } = useCart();
  const priceOf = usePricing();
  const price = product ? priceOf(product) : null;

  useEffect(() => {
    setLoading(true);
    setQty(1);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [slug]);

  const inCart = product ? items.find((i) => i.product_id === product.id) : null;

  const addToCart = () => {
    if (!product) return;
    addItem(product, qty);
    toast({ title: 'Aggiunto al carrello', description: `${product.name} × ${qty}` });
  };

  const buyNow = () => {
    addToCart();
    navigate('/carrello');
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12 text-neutral-500">Caricamento...</div>;
  if (!product) return (
    <div className="max-w-7xl mx-auto px-4 py-12 text-center">
      <AlertCircle size={48} className="text-neutral-400 mx-auto mb-4" />
      <h2 className="text-xl font-bold">Prodotto non trovato</h2>
      <Link to="/noleggio" className="inline-block mt-4 text-[#d62828] font-bold">Torna al catalogo</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <nav className="flex items-center text-sm text-neutral-500 mb-4 flex-wrap">
        <Link to="/" className="hover:text-[#d62828]">Home</Link>
        <ChevronRight size={14} className="mx-2" />
        <Link to="/noleggio" className="hover:text-[#d62828]">Noleggio</Link>
        {product.category_name && (
          <>
            <ChevronRight size={14} className="mx-2" />
            <span className="hover:text-[#d62828]">{product.category_name}</span>
          </>
        )}
        <ChevronRight size={14} className="mx-2" />
        <span className="text-neutral-800 font-medium">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="bg-neutral-50 rounded-lg overflow-hidden aspect-square flex items-center justify-center p-4">
          {product.image_url ? (
            <img src={resolveImageUrl(product.image_url)} alt={product.name} className="max-w-full max-h-full w-auto h-auto object-contain" />
          ) : (
            <Package size={120} className="text-neutral-300" />
          )}
        </div>

        <div>
          {product.brand && (
            <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 font-bold">
              <Building2 size={14} /> {product.brand}
            </span>
          )}
          <h1 className="text-3xl md:text-4xl font-black brand-text text-neutral-900 mt-2">{product.name}</h1>
          {product.short_description && <p className="text-lg text-neutral-700 mt-3">{product.short_description}</p>}

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            {product.available ? (
              <span className="inline-flex items-center gap-1 text-sm font-bold text-green-700 bg-green-50 px-3 py-1 rounded-full">
                <CheckCircle2 size={14} /> Disponibile
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-sm font-bold text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full">
                Su richiesta
              </span>
            )}
            {product.sku && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                <Tag size={12} /> SKU: {product.sku}
              </span>
            )}
            {product.category_name && (
              <span className="text-xs text-neutral-500">Categoria: {product.category_name}</span>
            )}
          </div>

          {product.price_per_day > 0 && (
            <div className="mt-6 p-5 bg-neutral-50 rounded-lg border border-neutral-200 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500 font-bold">Tariffa Giornaliera</p>
                {price.hasDiscount ? (
                  <>
                    <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                      <span className="text-lg font-medium text-neutral-400 line-through" data-testid="product-detail-original-price">
                        € {price.original.toFixed(2)}
                      </span>
                      <span className="text-3xl font-black text-[#d62828] brand-text" data-testid="product-detail-discounted-price">
                        € {price.discounted.toFixed(2)}
                        <span className="text-sm font-medium text-neutral-600"> /giorno</span>
                      </span>
                      <span
                        data-testid="product-detail-discount-badge"
                        className="bg-[#d62828] text-white text-xs font-black px-2 py-1 rounded"
                      >
                        -{Math.round(price.percent)}%
                      </span>
                    </div>
                    <p className="text-xs text-green-700 font-medium mt-1">Sconto cliente applicato · Risparmi € {(price.original - price.discounted).toFixed(2)} /giorno</p>
                  </>
                ) : (
                  <p className="text-3xl font-black text-[#d62828] brand-text mt-1" data-testid="product-detail-original-price">
                    € {product.price_per_day.toFixed(2)}
                    <span className="text-sm font-medium text-neutral-600"> /giorno</span>
                  </p>
                )}
              </div>
              <div className="border-t border-neutral-200 pt-3">
                <p className="text-xs uppercase tracking-wider text-neutral-500 font-bold">Tariffa Week-end</p>
                {price.hasDiscount ? (
                  <div className="flex items-baseline gap-3 mt-1 flex-wrap">
                    <span className="text-base font-medium text-neutral-400 line-through" data-testid="product-detail-weekend-original">
                      € {price.weekendOriginal.toFixed(2)}
                    </span>
                    <span className="text-2xl font-black text-[#d62828] brand-text" data-testid="product-detail-weekend-discounted">
                      € {price.weekendDiscounted.toFixed(2)}
                      <span className="text-sm font-medium text-neutral-600"> /week-end</span>
                    </span>
                  </div>
                ) : (
                  <p className="text-2xl font-black text-neutral-800 brand-text mt-1" data-testid="product-detail-weekend-original">
                    € {price.weekendOriginal.toFixed(2)}
                    <span className="text-sm font-medium text-neutral-600"> /week-end</span>
                  </p>
                )}
                {price.weekendIsAuto && (
                  <p className="text-[11px] text-neutral-500 mt-1">Prezzo week-end calcolato (giornaliero × 1,5)</p>
                )}
              </div>
            </div>
          )}

          {/* Add to cart actions */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="flex items-center border border-neutral-300 rounded-lg">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2.5 hover:bg-neutral-50" aria-label="Diminuisci">
                <Minus size={16} />
              </button>
              <span className="px-4 font-bold min-w-[3rem] text-center">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2.5 hover:bg-neutral-50" aria-label="Aumenta">
                <Plus size={16} />
              </button>
            </div>
            <button onClick={addToCart} className="px-5 py-2.5 border-2 border-[#d62828] text-[#d62828] font-bold rounded hover:bg-[#d62828] hover:text-white transition-colors flex items-center gap-2">
              <ShoppingCart size={18} /> Aggiungi al carrello
            </button>
            <button onClick={buyNow} className="px-5 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] transition-colors">
              Richiedi preventivo
            </button>
          </div>
          {inCart && (
            <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
              <CheckCircle2 size={12} /> Già nel carrello: {inCart.quantity} pz · <Link to="/carrello" className="underline font-bold">Vai al carrello</Link>
            </p>
          )}

          {product.description && (
            <div className="mt-8">
              <h3 className="font-bold text-neutral-800 mb-2">Descrizione</h3>
              <p className="text-neutral-700 leading-relaxed whitespace-pre-line">{product.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
